import {
  AmoreResponseJSON,
  CucinaResponseJSON,
  NomiNumeriResponseJSON,
  OroscopoResponseJSON,
  PsicolotticoResponseJSON
} from '../types/output-data'
import {
  fetchMockedAmoreResponseJSON,
  fetchMockedCucinaOptionsForStep,
  fetchMockedCucinaRicettaResponseJSON,
  fetchMockedNomiResponseJSON,
  fetchMockedOroscopoResponseJSON,
  fetchMockedPsicolotticoResponseJSON
} from '../utils/mock-utils'
import {
  AmoreRequestJSON,
  CucinaRequestJSON,
  OroscopoRequestJSON,
  PsicolotticoRequestJSON
} from '../types/input-data'
import { ERROR_CAUSE, TEST_PSICOLOTTICO } from '../types/enums'
import { AmoreFormData } from '../types/numeri-amore'

const isMock = process.env.REACT_APP_MOCK === 'true'

// Helper per gestire la risposta HTTP
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorDetails = await response.text()
    let errorCause = ERROR_CAUSE.NotOk
    switch (response.status) {
      case 400:
        errorCause = ERROR_CAUSE.BadRequest
        break
    }

    throw new Error(`Errore API: ${response.status} - ${errorDetails}`, {
      cause: errorCause
    })
  }
  return response.json()
}

// NOMI
export const getNomiNumeriResponse = async (
  nameToSearch: string
): Promise<NomiNumeriResponseJSON> => {
  if (isMock) {
    return await fetchMockedNomiResponseJSON(nameToSearch)
  } else {
    try {
      const response = await fetch(`${process.env.REACT_APP_ROOT_API}/name`, {
        method: 'POST',
        body: JSON.stringify({ name: nameToSearch })
      })
      return await handleResponse(response)
    } catch (error) {
      throw error
    }
  }
}

// PSICOLOTTICO
export const getPiscolotticoResponse = async (
  selectedTest: TEST_PSICOLOTTICO,
  questions: string[],
  answers: string[]
): Promise<PsicolotticoResponseJSON> => {
  if (isMock) {
    return await fetchMockedPsicolotticoResponseJSON(selectedTest)
  } else {
    try {
      const requestBody: PsicolotticoRequestJSON = {
        chosenTest: selectedTest,
        question1: questions[0],
        question2: questions[1],
        question3: questions[2],
        question4: questions[3],
        question5: questions[4],
        question6: questions[5],
        question7: questions[6],
        question8: questions[7],
        answer1: answers[0],
        answer2: answers[1],
        answer3: answers[2],
        answer4: answers[3],
        answer5: answers[4],
        answer6: answers[5],
        answer7: answers[6],
        answer8: answers[7]
      }

      const response = await fetch(`${process.env.REACT_APP_ROOT_API}/psychological`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      })
      return await handleResponse(response)
    } catch (error) {
      throw error
    }
  }
}

// AMORE
export const getAmoreNumbersResponse = async (
  dataToSubmit: AmoreFormData
): Promise<AmoreResponseJSON> => {
  if (isMock) {
    return await fetchMockedAmoreResponseJSON()
  } else {
    try {
      const dayFormatted =
        dataToSubmit.day?.length === 1 ? '0' + dataToSubmit.day : dataToSubmit.day
      const monthFormatted =
        dataToSubmit.month?.length === 1 ? '0' + dataToSubmit.month : dataToSubmit.month
      const requestBody: AmoreRequestJSON = {
        date: `${dayFormatted}/${monthFormatted}/${dataToSubmit.year}`,
        place: dataToSubmit.region ?? ''
      }

      const response = await fetch(`${process.env.REACT_APP_ROOT_API}/love`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      })
      return await handleResponse(response)
    } catch (error) {
      throw error
    }
  }
}

// CUCINA - Opzioni
export const getCucinaOptionsForStepResponse = async (
  step: number,
  course: string,
  firstIngredient?: string,
  secondIngredient?: string
): Promise<CucinaRequestJSON> => {
  if (isMock) {
    return await fetchMockedCucinaOptionsForStep(step)
  } else {
    try {
      const requestBody: CucinaRequestJSON = {
        course,
        firstIngredient,
        secondIngredient
      }

      let endpoint = `${process.env.REACT_APP_ROOT_API}/`

      switch (step) {
        // da richiedere primo ingrediente
        case 1:
          endpoint += 'firstIngredient'
          break
        // secondo ingrediente
        case 2:
          endpoint += 'secondIngredient'
          break
        // ricette
        case 3:
          endpoint += 'recipe'
          break
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      })
      return await handleResponse(response)
    } catch (error) {
      throw error
    }
  }
}

// CUCINA - Ricetta
export const getCucinaRecipeResponse = async (
  course: string,
  firstIngredient: string,
  secondIngredient: string,
  recipe: string
): Promise<CucinaResponseJSON> => {
  if (isMock) {
    return await fetchMockedCucinaRicettaResponseJSON()
  } else {
    try {
      const requestBody: CucinaRequestJSON = {
        course,
        firstIngredient,
        secondIngredient,
        recipe
      }

      const response = await fetch(`${process.env.REACT_APP_ROOT_API}/cook`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      })
      return await handleResponse(response)
    } catch (error) {
      throw error
    }
  }
}

// OROSCOPO
export const getOroscopoResponse = async (
  starSign: string,
  currentDate: string
): Promise<OroscopoResponseJSON> => {
  if (isMock) {
    return await fetchMockedOroscopoResponseJSON(starSign)
  } else {
    try {
      const requestBody: OroscopoRequestJSON = {
        starSign: starSign,
        date: currentDate
      }

      const response = await fetch(`${process.env.REACT_APP_ROOT_API}/horoscope`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      })
      return await handleResponse(response)
    } catch (error) {
      throw error
    }
  }
}
